import { FixtureDraft, FixtureParticipant } from "./fixtureTypes";
import { PICKLEBALL_EVENT_CATEGORIES } from './pickleballFixture';

const sportKey = (sport: string) => sport.replace(/[\s_-]/g, "").toLowerCase();
const isRace = (sport: string) => ["swimming", "skating"].includes(sportKey(sport));
const isEventDoubles = (eventCategory?: string) => /double|mixed/i.test(eventCategory || "");

const idsFor = (participant: FixtureParticipant) => participant.players || [];

export function validateFixtureDraft(
  sport: string,
  draft: FixtureDraft
): string | undefined {
  const key = sportKey(sport);
  const participants = Array.isArray(draft.participants) ? draft.participants : [];

  if (key === 'pickleball' && !draft.eventCategory) {
    return 'Pickleball requires an event category.';
  }
  if (key === 'pickleball' && !PICKLEBALL_EVENT_CATEGORIES.some((option) => option.value === draft.eventCategory)) {
    return 'Pickleball event category is invalid.';
  }
  if (participants.length < 2) return "At least two participants are required.";
  if (!isRace(sport) && participants.length !== 2) return "This sport requires exactly two participants.";
  if (participants.some((participant, index) => participant.slot !== index + 1)) {
    return "Participant slots must be sequential, starting from 1.";
  }

  const seenIds = new Set<string>();
  for (const participant of participants) {
    if (key === "football") {
      if (!participant.team) return "Football requires one team on each side.";
      if (seenIds.has(participant.team)) return "A team cannot occupy more than one participant slot.";
      seenIds.add(participant.team);
      continue;
    }

    if (participant.team && ["padel", "pickleball", "tennis"].includes(key)) {
      return `${sport} player formats cannot use team references.`;
    }

    const playerIds = idsFor(participant);
    if (!participant.team && !playerIds.length) continue;

    if (participant.team && !playerIds.length) {
      if (key === "badminton" || key === "tabletennis" || key === "basketball" || key === "cricket" || key === "generic") continue;
      return `${sport} requires player participants for this format.`;
    }

    const requiredPlayers = key === "padel" ? undefined : (isEventDoubles(draft.eventCategory) ? 2 : 1);
    if (!participant.team && requiredPlayers && playerIds.length !== requiredPlayers) {
      return requiredPlayers === 2
        ? `${sport} requires exactly two players on each side.`
        : `${sport} requires exactly one player on each side.`;
    }

    for (const id of playerIds) {
      if (seenIds.has(id)) return "A player cannot appear in more than one participant slot.";
      seenIds.add(id);
    }
  }

  if (isRace(sport) && draft.format === "player" && participants.some((participant) => idsFor(participant).length !== 1)) {
    return `${sport} requires exactly one player per lane.`;
  }

  if (key === "chess" && draft.format === "team" && participants.some((participant) => idsFor(participant).length < 2)) {
    return "Chess team format requires at least two players per side.";
  }

  return undefined;
}

export function buildFixtureRequest(sport: string, draft: FixtureDraft): Record<string, unknown> {
  const error = validateFixtureDraft(sport, draft);
  if (error) throw new Error(error);

  const participants = Array.isArray(draft.participants) ? draft.participants : [];
  const {
    format: _format,
    participants: _participants,
    teamA: _teamA,
    teamB: _teamB,
    playerA: _playerA,
    playerB: _playerB,
    side1: _side1,
    side2: _side2,
    preserveEmptyParticipants,
    ...common
  } = draft as FixtureDraft & Record<string, unknown>;
  const trimmedCommon = {
    ...common,
    ...(!common.stage ? { stage: undefined } : {}),
    ...(typeof common.title === "string" ? { title: common.title.trim() } : {}),
    ...(typeof common.subtitle === "string" ? { subtitle: common.subtitle.trim() } : {}),
  };
  if (sportKey(sport) === "football") {
    return {
      ...trimmedCommon,
      ...(participants[0]?.team ? { teamA: participants[0].team } : {}),
      ...(participants[1]?.team ? { teamB: participants[1].team } : {}),
    };
  }

  const activeParticipants = participants
    .map(({ slot, team, players }) => ({
      slot,
      ...(team ? { team } : {}),
      ...(players?.length ? { players } : {}),
    }))
    .filter((participant) => participant.team || participant.players?.length);

  return {
    ...trimmedCommon,
    ...(activeParticipants.length || preserveEmptyParticipants
      ? { participants: activeParticipants }
      : {}),
  };
}
